import { type Deck, findBlock, flattenDeck } from "@hali-shares/deck-schema";
import { useEffect, useRef, useState } from "react";
import { SlidePreview } from "../../deck/SlidePreview";
import { usePreference } from "../../lib/preferences";

/** The stops a slide can show: a flow's overview and zooms, a screenshot's fit and pins. */
function stopLabels(deck: Deck, slideIndex: number): string[] {
  const slide = flattenDeck(deck)[slideIndex];
  if (slide?.kind !== "block") return [];
  const block = findBlock(deck, slide.blockId);
  if (block?.type === "flow")
    return ["Overview", ...block.nodes.filter((n) => n.detail).map((n) => `Zoom: ${n.label || "step"}`)];
  if (block?.type === "screenshot") return ["Fit", ...block.pins.map((_, i) => `Pin ${i + 1}`)];
  if (block?.type === "timeline" && block.reveal === "steps") return block.items.map((i) => i.label || i.when);
  return [];
}

function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(552);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => e && setWidth(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Live preview of the slide being edited, with its zoom stops and (for polls) the revealed view. */
export function PreviewPanel({
  deck,
  slideIndex,
  sticky = false,
}: {
  deck: Deck;
  slideIndex: number;
  /** Stay at the top of the drawer while the form scrolls under it. */
  sticky?: boolean;
}) {
  const [open, setOpen] = usePreference("preview-open", true);
  const [stop, setStop] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [ref, width] = useWidth<HTMLDivElement>();
  const stops = stopLabels(deck, slideIndex);
  const slide = flattenDeck(deck)[slideIndex];
  const block = slide?.kind === "block" ? findBlock(deck, slide.blockId) : undefined;
  const votable = block?.type === "poll" || block?.type === "quiz";
  const current = Math.min(stop, Math.max(0, stops.length - 1));

  // Tell the scrolling drawer how tall the pinned preview is, so focused fields scroll into view below it.
  useEffect(() => {
    const el = ref.current;
    const parent = el?.parentElement;
    if (!el || !parent || !sticky) return;
    const ro = new ResizeObserver(() => parent.style.setProperty("--preview-h", `${el.offsetHeight + 12}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      parent.style.removeProperty("--preview-h");
    };
  }, [ref, sticky]);

  const toggle = () => {
    setOpen(!open);
  };

  return (
    <section className={`preview-panel${sticky ? " is-sticky" : ""}`} ref={ref} aria-label="Slide preview">
      <div className="preview-head">
        <button type="button" className="preview-toggle" aria-expanded={open} onClick={toggle}>
          {open ? "Hide preview" : "Show preview"}
        </button>
        {open && slideIndex >= 0 && <span className="col-num">Slide {slideIndex + 1}</span>}
      </div>
      {open && slideIndex >= 0 && (
        <>
          <SlidePreview deck={deck} slideIndex={slideIndex} stop={current} width={width} revealed={revealed} />
          {(stops.length > 1 || votable) && (
            <div className="preview-stops" role="group" aria-label="Preview step">
              {stops.length > 1 &&
                stops.map((label, i) => (
                  <button
                    key={i}
                    type="button"
                    className="preview-stop"
                    aria-pressed={i === current}
                    onClick={() => setStop(i)}
                  >
                    {label}
                  </button>
                ))}
              {votable && (
                <>
                  <button
                    type="button"
                    className="preview-stop"
                    aria-pressed={!revealed}
                    onClick={() => setRevealed(false)}
                  >
                    Voting
                  </button>
                  <button
                    type="button"
                    className="preview-stop"
                    aria-pressed={revealed}
                    onClick={() => setRevealed(true)}
                  >
                    {block?.type === "quiz" ? "Answer shown" : "Results shown"}
                  </button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
